/**
 * Algolia Adapter
 * Wraps the Algolia SDK to provide a clean abstraction layer
 *
 * Benefits:
 * - Centralized configuration
 * - Easy to swap search providers (Elasticsearch, MeiliSearch, etc.)
 * - Consistent error handling
 * - Feature availability checks
 */
import { algoliasearch, type SearchClient } from "algoliasearch";
import { liteClient as algoliasearchLite, type LiteClient } from "algoliasearch/lite";

import { algoliaConfig } from "@/lib/config/algolia-config";
import { clientEnv, features, serverEnv } from "@/lib/config/env";

// Singleton instances
let _searchClient: SearchClient | null = null;
let _liteClient: LiteClient | null = null;

/**
 * Check if Algolia is available
 */
export function isAlgoliaAvailable(): boolean {
  return features.hasAlgolia;
}

/**
 * Get Algolia configuration
 */
export function getAlgoliaConfig() {
  return {
    appId: clientEnv.NEXT_PUBLIC_ALGOLIA_APP_ID || algoliaConfig.appId,
    searchKey: clientEnv.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY || algoliaConfig.searchKey,
    adminKey: serverEnv.ALGOLIA_ADMIN_KEY || algoliaConfig.adminKey,
    indices: algoliaConfig.indices,
  };
}

/**
 * Get the Algolia search client (server-side with admin key)
 * Used for indexing operations
 */
export function getAlgoliaClient(): SearchClient | null {
  if (!isAlgoliaAvailable()) {
    return null;
  }

  if (_searchClient === null) {
    const config = getAlgoliaConfig();
    _searchClient = algoliasearch(config.appId, config.adminKey);
  }

  return _searchClient;
}

/**
 * Get the Algolia lite client (client-side with search key)
 * Used for search operations in the browser
 */
export function getAlgoliaLiteClient(): LiteClient | null {
  if (!isAlgoliaAvailable()) {
    return null;
  }

  if (_liteClient === null) {
    const config = getAlgoliaConfig();
    _liteClient = algoliasearchLite(config.appId, config.searchKey);
  }

  return _liteClient;
}

/**
 * Search an index
 */
export async function searchIndex<T = Record<string, unknown>>(
  indexName: string,
  query: string,
  options?: {
    hitsPerPage?: number;
    page?: number;
    filters?: string;
    facets?: string[];
    attributesToRetrieve?: string[];
  }
): Promise<{
  hits: T[];
  nbHits: number;
  page: number;
  nbPages: number;
} | null> {
  const client = getAlgoliaClient();
  if (!client) {
    return null;
  }

  try {
    const result = await client.searchSingleIndex({
      indexName,
      searchParams: {
        query,
        hitsPerPage: options?.hitsPerPage ?? 20,
        page: options?.page ?? 0,
        filters: options?.filters,
        facets: options?.facets,
        attributesToRetrieve: options?.attributesToRetrieve,
      },
    });

    return {
      hits: result.hits as T[],
      nbHits: result.nbHits ?? 0,
      page: result.page ?? 0,
      nbPages: result.nbPages ?? 0,
    };
  } catch (error) {
    console.error("[Algolia Adapter] Search failed:", error);
    throw error;
  }
}

/**
 * Index a single object
 */
export async function indexObject(
  indexName: string,
  object: Record<string, unknown> & { objectID: string }
): Promise<{ taskID: number } | null> {
  const client = getAlgoliaClient();
  if (!client) {
    return null;
  }

  try {
    const result = await client.saveObject({
      indexName,
      body: object,
    });
    return { taskID: result.taskID };
  } catch (error) {
    console.error("[Algolia Adapter] Index object failed:", error);
    throw error;
  }
}

/**
 * Index multiple objects
 */
export async function indexObjects(
  indexName: string,
  objects: Array<Record<string, unknown> & { objectID: string }>
): Promise<{ taskID: number } | null> {
  const client = getAlgoliaClient();
  if (!client) {
    return null;
  }

  try {
    const result = await client.saveObjects({
      indexName,
      objects,
    });
    // Return the first taskID (batch operations may return multiple)
    return { taskID: Array.isArray(result) ? (result[0]?.taskID ?? 0) : 0 };
  } catch (error) {
    console.error("[Algolia Adapter] Index objects failed:", error);
    throw error;
  }
}

/**
 * Delete an object from index
 */
export async function deleteObject(indexName: string, objectID: string): Promise<{ taskID: number } | null> {
  const client = getAlgoliaClient();
  if (!client) {
    return null;
  }

  try {
    const result = await client.deleteObject({
      indexName,
      objectID,
    });
    return { taskID: result.taskID };
  } catch (error) {
    console.error("[Algolia Adapter] Delete object failed:", error);
    throw error;
  }
}

/**
 * Configure index settings
 */
export async function configureIndex(
  indexName: string,
  settings: {
    searchableAttributes?: string[];
    attributesForFaceting?: string[];
    customRanking?: string[];
  }
): Promise<{ taskID: number } | null> {
  const client = getAlgoliaClient();
  if (!client) {
    return null;
  }

  try {
    const result = await client.setSettings({
      indexName,
      indexSettings: settings,
    });
    return { taskID: result.taskID };
  } catch (error) {
    console.error("[Algolia Adapter] Configure index failed:", error);
    throw error;
  }
}

/**
 * Re-export utilities
 */
export { getPatientTimelineIndex, getPatientIdFromIndex } from "@/lib/config/algolia-config";
